import React, { useEffect, useState } from 'react';
import { Button, Card, Descriptions, Modal, Space, Spin, Table, Tag } from 'antd';
import orderService from '../../services/order';
import Column from 'antd/lib/table/Column';
import { PrinterOutlined, ShopOutlined } from '@ant-design/icons';
import moment from 'moment';
import shopService from '../../services/shop';
import numberToPrice from '../../helpers/numberToPrice';
import { useTranslation } from 'react-i18next';

const PreviewInfo = ({ orderId, handleClose }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [list, setList] = useState([]);
  const [shopList, setShopList] = useState([]);

  console.log('data from edite', data)
  function fetchOrderDetails() {
    setLoading(true);
    orderService
      .getById(orderId)
      .then((res) => {
        setData(res.data);
        const items = res.data.details.map((item) =>
          item.order_stocks.map((el) => ({
            ...el.stock,
            ...el,
            shop_id: item.shop_id,
          }))
        );
        const products = items.flat();
        setList(products);
      })
      .finally(() => setLoading(false));
  }

  function fetchShops() {
    shopService.get().then(({ data }) => setShopList(data));
  }

  useEffect(() => {
    if (orderId) {
      fetchOrderDetails();
      fetchShops();
    }
  }, [orderId]);

  function calculateProductsPrice() {
    return data?.details.reduce((total, item) => (total += item.price), 0);
  }

  function calculateTax() {
    return data?.details.reduce((total, item) => (total += item.tax), 0);
  }

  function calculateCoupon() {
    return data?.details.reduce(
      (total, item) => (total += item.coupon?.price),
      0
    );
  }

  function calculateDeliveryPrice() {
    return data?.details.reduce(
      (total, item) => (total += item.delivery_fee),
      0
    );
  }

  return (
    <Modal
      visible={!!orderId}
      title={t('order.created.successfully')}
      onOk={handleClose}
      onCancel={handleClose}
      footer={[
        <Button onClick={handleClose}>{t('back')}</Button>,
        <Button type='primary' onClick={() => window.print()}>
          <PrinterOutlined type='printer' />
          <span className='ml-1'>{t('print')}</span>
        </Button>,
      ]}
      style={{ minWidth: '80vw' }}
    >
      <div className='py-4 order-preview'>
        {loading ? (
          <div className='w-100 text-center'>
            <Spin />
          </div>
        ) : (
          <Card>
            <div className='d-flex justify-content-between mt-3'>
              <div>
                <h2 className='mb-1 font-weight-semibold'>
                  {t('invoice')} #{data?.id}
                </h2>
                <p>{moment(data?.created_at).format('DD/M/YYYY')}</p>
                <address>
                  <p>
                    <span>
                      {t('delivery.type')}:{' '}
                      {data?.details[0]?.delivery_type?.translation?.title}
                    </span>
                    <br />
                    <span>
                      {t('delivery.address')}:{' '}
                      {data?.details[0]?.delivery_address?.address}
                    </span>
                    <br />
                    <span>
                      {t('delivery.date')}: {data?.details[0]?.delivery_date}{' '}
                      {data?.details[0]?.delivery_time}
                    </span>
                    <br />
                    <span>
                      {t('note')}: {data?.note}
                    </span>
                  </p>
                </address>
              </div>
              <address>
                <p>
                  <span className='font-weight-semibold text-dark font-size-md'>
                    {data?.user?.firstname} {data?.user?.lastname}
                  </span>
                  <br />
                  <span>
                    {t('phone')}: {data?.user?.phone}
                  </span>
                  <br />
                  <span>
                    {t('email')}: {data?.user?.email}
                  </span>
                </p>
              </address>
            </div>

            {/* Merchant Details Section */}
            {data?.merchant?.id && (
              <Card
                title={
                  <Space>
                    <ShopOutlined />
                    <span>{t('merchant.details')}</span>
                  </Space>
                }
                className='mb-3 mt-3'
                size='small'
              >
                <Descriptions bordered size='small' column={2}>
                  <Descriptions.Item label={t('merchant.name')} span={1}>
                    {data?.merchant?.name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('pharmacy.code')} span={1}>
                    {data?.merchant?.pharmacy_code || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('pharmacist.name')} span={1}>
                    {data?.merchant?.pharmacist_name || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('commercial.registration.number')} span={1}>
                    {data?.merchant?.crn || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('tax.card.number')} span={1}>
                    {data?.merchant?.tax_card_number || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('license.number')} span={1}>
                    {data?.merchant?.license_number || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('governorate')} span={1}>
                    {data?.merchant?.governorate || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('area')} span={1}>
                    {data?.merchant?.area || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('address')} span={2}>
                    {data?.merchant?.address || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('phone')} span={1}>
                    {data?.merchant?.phone || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('landline.number')} span={1}>
                    {data?.merchant?.landline_number || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('status')} span={1}>
                    <Tag color={
                      data?.merchant?.status === 'approved' ? 'green' :
                      data?.merchant?.status === 'rejected' ? 'red' :
                      data?.merchant?.status === 'cancelled' ? 'default' : 'blue'
                    }>
                      {t(data?.merchant?.status || 'new')}
                    </Tag>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            <div className='mt-4'>
              <Table
                dataSource={list}
                rowKey={(record) => record.id}
                pagination={false}
                className='mb-5'
              >
                <Column title='No.' dataIndex='id' key='id' />
                <Column
                  title={t('product')}
                  dataIndex='product'
                  key='product'
                  render={(product) => product?.translation?.title}
                />
                <Column
                  title={t('shop')}
                  dataIndex='shop_id'
                  key='shop_id'
                  render={(shop_id) =>
                    shopList.find((item) => item.id === shop_id)?.translation
                      ?.title
                  }
                />
                <Column
                  title={t('quantity')}
                  dataIndex='quantity'
                  key='quantity'
                />
                <Column
                  title={t('discount')}
                  dataIndex='discount'
                  key='discount'
                  render={(discount) =>
                    numberToPrice(discount, data?.currency?.symbol)
                  }
                />
                <Column
                  title={t('price')}
                  dataIndex='total_price'
                  key='total_price'
                  render={(total_price) =>
                    numberToPrice(total_price, data?.currency?.symbol)
                  }
                />
              </Table>
              <div className='d-flex justify-content-end'>
                <div className='text-right '>
                  <div className='border-bottom'>
                    <p className='mb-2'>
                      <span>{t('sub-total.amount')}: </span>
                      {numberToPrice(
                        calculateProductsPrice(),
                        data?.currency?.symbol
                      )}
                    </p>
                    <p>
                      {t('delivery.price')} :{' '}
                      {numberToPrice(
                        calculateDeliveryPrice(),
                        data?.currency?.symbol
                      )}
                    </p>
                    <p>
                      {t('tax')} :{' '}
                      {numberToPrice(calculateTax(), data?.currency?.symbol)}
                    </p>
                    <p>
                      {t('coupon')} :{' '}
                      {numberToPrice(calculateCoupon(), data?.currency?.symbol)}
                    </p>
                  </div>
                  <h2 className='font-weight-semibold mt-3'>
                    <span className='mr-1'>{t('grand.total')}: </span>
                    {numberToPrice(data?.price, data?.currency?.symbol)}
                  </h2>
                </div>
              </div>
            </div>
          </Card>
        )}
      </div>
    </Modal>
  );
};

export default PreviewInfo;
